<template>
    <v-container fluid>
        <div v-if="!loading">
            <v-row>
                <v-col>
                    <h4 class="text-h4">{{$t("stock_transfer_voucher")}} #{{data.code}}</h4>
                    <p class="text-subtitle-1">{{$t("transfer_stock_items_between_warehouses")}}</p>
                    <v-divider></v-divider>
                </v-col>
            </v-row>
            <v-row>
                <!-- TODO: Hide and Show actions according to state_code after backend finish -->
                <v-col cols="12" md="3" v-if="isPostable">
                    <v-btn block color="primary" @click="handlePost" :loading="postLoading">
                        <v-icon left>mdi-post</v-icon>
                        {{$t("post_to_ledger")}}
                    </v-btn>
                </v-col>
                <v-col cols="12" md="3" v-if="isReceivable">
                    <v-btn block color="primary" @click="handleReceipt">
                        <v-icon left>mdi-arrow-down</v-icon>
                        {{$t("stock_transfer_receipt")}}
                    </v-btn>
                </v-col>
                <v-col cols="12" md="3" v-if="isPrintable">
                    <v-btn block depressed @click="handlePrint" :loading="printLoading">
                        <v-icon left>mdi-printer</v-icon>
                        {{$t("print")}}
                    </v-btn>
                </v-col>
                <v-col cols="12" md="3" v-if="isEditable">
                    <v-btn block depressed @click="handleUpdate">
                        <v-icon left>mdi-pencil</v-icon>
                        {{$t("edit")}}
                    </v-btn>
                </v-col>
                <v-col cols="12" md="3" v-if="isDeletable">
                    <v-btn block depressed color="error--text" @click="handleDelete" :loading="deleteLoading">
                        <v-icon left>mdi-delete</v-icon>
                        {{$t("delete")}}
                    </v-btn>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>{{$t("code")}}</th>
                                <td class="font-weight-bold">{{data.code}}</td>
                            </tr>
                            <tr>
                                <th>{{$t("date")}}</th>
                                <td>{{data.date}}</td>
                            </tr>
                            <tr>
                                <th>{{$t("source_warehouse")}}</th>
                                <td>{{data.source_warehouse ? data.source_warehouse.display_name : ""}}</td>
                            </tr>
                            <tr>
                                <th>{{$t("target_warehouse")}}</th>
                                <td class="font-weight-bold">{{data.target_warehouse ? data.target_warehouse.display_name : ""}}</td>
                            </tr>
                            <tr>
                                <th>{{$t("state")}}</th>
                                <td>
                                    <PStatus :state="data.state"/>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    <v-simple-table dense>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>{{$t("item")}}</th>
                                <th>{{$t("quantity")}}</th>
                                <th>{{$t("measure_unit")}}</th>
                                <th>{{$t("price")}}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(d, i) in data.lines" :key="i">
                                <td>{{i+1}}</td>
                                <td>{{d.item.display_name}}</td>
                                <td>{{d.quantity}}</td>
                                <td>{{d.measure_unit.abbreviation}}</td>
                                <td>{{d.price}}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>{{$t("remarks")}}</th>
                                <td>{{data.notes}}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>{{$t("created_by")}}</th>
                                <td>
                                    <span v-if="data && data.created_by">
                                        {{data.created_by.first_name}} {{data.created_by.last_name}}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <th>{{$t("created_at")}}</th>
                                <td>{{data.created_at}}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
                <v-col>
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>{{$t("updated_by")}}</th>
                                <td>
                                    <span v-if="data && data.updated_by">
                                        {{data.updated_by.first_name}} {{data.updated_by.last_name}}
                                    </span>
                                    <span v-else>
                                        --
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <th>{{$t("updated_at")}}</th>
                                <td>
                                    <span v-if="data && data.updated_at">
                                            {{data.updated_at}}
                                    </span>
                                    <span v-else>
                                        --
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
                <v-col>
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>{{$t("posted_by")}}</th>
                                <td>
                                    <span v-if="data && data.posted_by">
                                        {{data.posted_by.first_name}} {{data.posted_by.last_name}}
                                    </span>
                                    <span v-else>
                                        --
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <th>{{$t("posted_at")}}</th>
                                <td>
                                    <span v-if="data && data.posted_at">
                                            {{data.posted_at}}
                                    </span>
                                    <span v-else>
                                        --
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
            </v-row>
        </div>
        <div v-else>
            <v-row>
                <v-col>
                    <v-skeleton-loader type="heading" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-divider></v-divider>
                </v-col>
            </v-row>
            <v-row>
                <v-col class="d-flex">
                    <v-skeleton-loader type="button" class="my-4 me-4"/>
                    <v-skeleton-loader type="button" class="my-4"/>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="4">
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                </v-col>
                <v-col cols="12" md="4">
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                </v-col>
                <v-col cols="12" md="4">
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                </v-col>
            </v-row>
        </div>
        <PAlert v-model="alert.value" v-bind="alert"/>
    </v-container>
</template>

<script>
    import permissions from '../../../data/permissions';
import stockTransferAPI from '../../../services/stockJournal/stockTransfer';
import PAlert from '../../../components/common/PAlert.vue';
import PStatus from './../../../components/common/PStatus.vue';
import authorize from '../../../utils/authorize';

export default {
    components: {
        PAlert,
        PStatus
    },
    data() {
        return {
            id: null,
            loading: false,
            deleteLoading: false,
            printLoading: false,
            postLoading: false,
            data: {},
            alert:{
                value: false,
                content: 'Are you sure?',
                callback: null
            },
            perm: permissions,
            warehouseId: null
        }
    },
    computed: {
        isPostable() {
            return this.data && this.data.meta && this.data.meta.is_postable && this.hasPermission(this.perm.stock_transfer.post);
        },
        isReceivable() {
            return this.data && this.data.meta && this.data.target_warehouse &&
            // stock transfer voucher should logically able to be received.
            this.data.meta.is_receivable && 
            // user should be in different warehosue for performing this operation.
            this.data.target_warehouse.id == this.warehouseId &&
            // user should have the permission to create stock transfer receipt
            this.hasPermission(this.perm.stock_transfer_receipt.create) ;
        },
        isEditable() {
            return this.data && this.data.meta && this.data.meta.is_editable && this.hasPermission(this.perm.stock_transfer.update);
        },
        isDeletable() {
            return this.data && this.data.meta && this.data.meta.is_deletable && this.hasPermission(this.perm.stock_transfer.delete)
        },
        isPrintable() {
            return this.data && this.data.meta && this.data.meta.is_printable && this.hasPermission(this.perm.stock_transfer.print)
        }
    },
    methods: {
        fetch() {
            this.loading = true;
            stockTransferAPI.find(this.id).then(response => {
                this.data = response.data
            }).finally(() => {
                this.loading =false;
            })
        },
        handleDelete() {
            this.alert = {...this.alert, value: true, content: "Are you sure you want to delete?", callback: this.remove}
        },
        remove() {
            this.deleteLoading = true;
            stockTransferAPI.remove(this.id).then(() => {
                this.$router.replace({
                    name: "TransferIssueVoucherList"
                })
            }).finally(() => {
                this.deleteLoading = false;
            })
        },
        handleUpdate() {
            this.$router.push({
                name: "TransferIssueVoucherEdit",
                params: {
                    id: this.id
                }
            })
        },
        handlePost() {
            this.alert = {...this.alert, value: true, content: "Are you sure you want to post?", callback: this.post }
        },
        post() {
            this.postLoading = true;
            stockTransferAPI.post(this.id).then(response => {
                this.data = response.data;
                setTimeout(() => {
                    this.fetch();
                }, 800)
            }).finally(() => {
                this.postLoading = false;
            })
        },
        handlePrint() {
            this.printLoading = true;
            stockTransferAPI.print(this.id)
                .then(response => {
                    var printWindow = window.open('','','width=800,height=600');
                    printWindow.document.write(response.data);
                    printWindow.document.close();
                    printWindow.focus();
                    printWindow.print();
                    printWindow.close();
                }).finally(() => {
                    this.printLoading = false;
                })
        },
        handleReceipt() {
            this.$router.push({
                name: "TransferReceiptVoucherCreate",
                query: {
                    stock_transfer_id: this.id
                }
            })
        },
        hasPermission(code) {
            return authorize(code)
        }
    },
    created() {
        this.id = this.$route.params.id;
        this.warehouseId = this.$route.params.warehouseId;
        this.fetch();
    }
}
</script>

<style>

</style>